'use client';

import clsx from 'clsx';
import { type CSSProperties, type ComponentProps, useState } from 'react';
import ReactPlayer from 'react-player';

import LoadingSkeleton from '../LoadingSkeleton';
import styles from './index.module.scss';

/**
 * Type definition for the VideoPlayer component properties.
 */

type Props = ComponentProps<typeof ReactPlayer> & {
  fullScreen?: boolean;
  aspectRatio?: CSSProperties['aspectRatio'];
  roundCorners?: boolean;
  displayLoading?: boolean;
  className?: string;
};

/**
 * VideoPlayer component.
 *
 * A source URL can be provided via the `src` property.
 * The component also supports multiple source tags as children for more complex use cases.
 *
 * @see https://github.com/cookpete/react-player
 */

const VideoPlayer = ({
  fullScreen = false,
  aspectRatio,
  roundCorners = true,
  displayLoading = false,
  className,
  style,
  children,
  ...props
}: Props) => {
  const [isReady, setIsReady] = useState(false);

  const isLoading = displayLoading && !isReady;

  const classes = clsx(
    styles.wrapper,
    isReady && styles.ready,
    fullScreen && styles.fullScreen,
    roundCorners && styles.roundCorners,
    className
  );

  const inlineStyles: CSSProperties = {
    aspectRatio,
    ...style,
  };

  const handleReady = () => {
    setIsReady(true);
  };

  return (
    <div className={classes} style={inlineStyles}>
      {isLoading && <LoadingSkeleton width="100%" height="100%" className={styles.loading} />}
      <ReactPlayer width="100%" height="100%" onReady={handleReady} {...props}>
        {children}
      </ReactPlayer>
    </div>
  );
};

export default VideoPlayer;
